import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Typography,
  OutlinedInput,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Close,
  FilterList,
  Clear,
  Search,
  Delete,
  Add,
  DateRange,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  FilterModalActions,
  FilterModalHeader,
  filterModalContentSx,
  filterModalPaperProps,
} from "components/FilterModalChrome";

const CustomFilterModal = ({
  open,
  onClose,
  onApplyFilter,
  onClearFilter,
  data = [],
  cabangOptions = null,
  bulanOptions = null,
  sortableColumns = [],
}) => {
  const theme = useTheme();

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBulan, setSelectedBulan] = useState([]);
  const [selectedCabang, setSelectedCabang] = useState([]);

  // Date Range Filter
  const [tanggalAwal, setTanggalAwal] = useState(null);
  const [tanggalAkhir, setTanggalAkhir] = useState(null);

  // Sort state: array of { column, order }
  const [sortCriteria, setSortCriteria] = useState([
    { column: "", order: "asc" },
  ]);

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedBulan([]);
    setSelectedCabang([]);
    setTanggalAwal(null);
    setTanggalAkhir(null);
    setSortCriteria([{ column: "", order: "asc" }]);
    onClearFilter();
  };

  const getUniqueBulan = () => {
    if (bulanOptions) return [...bulanOptions].sort();
    return [
      ...new Set(
        data
          .map((row) => row.bulan)
          .filter((val) => val !== null && val !== undefined && val !== "")
      ),
    ].sort();
  };

  const getUniqueCabang = () => {
    if (cabangOptions) return [...cabangOptions].sort();
    return [
      ...new Set(
        data
          .map((row) => row.cabang)
          .filter((val) => val !== null && val !== undefined && val !== "")
      ),
    ].sort();
  };

  // Hanya tampilkan kolom yang belum dipilih
  const getAvailableColumns = (currentIndex) => {
    const usedColumns = sortCriteria
      .filter((_, idx) => idx !== currentIndex)
      .map((c) => c.column);
    return sortableColumns.filter((col) => !usedColumns.includes(col.value));
  };

  const handleSortChange = (index, key, value) => {
    const updated = [...sortCriteria];
    updated[index][key] = value;
    setSortCriteria(updated);
  };

  const applyFilters = () => {
    const filterConfig = {
      search: searchQuery,
      filter: {
        cabang: selectedCabang,
        bulan: selectedBulan,
      },
      dateRange: {
        tanggalAwal: tanggalAwal ? formatDateToYYYYMMDD(tanggalAwal) : null,
        tanggalAkhir: tanggalAkhir ? formatDateToYYYYMMDD(tanggalAkhir) : null,
      },
      sort: sortCriteria.filter((c) => c.column), // hanya kirim kriteria valid
    };
    onApplyFilter(filterConfig);
    onClose();
  };

  // Helper function to format date to YYYY-MM-DD
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (searchQuery) count++;
    if (selectedBulan.length > 0) count++;
    if (selectedCabang.length > 0) count++;
    if (tanggalAwal || tanggalAkhir) count++;
    if (sortCriteria.some((c) => c.column)) count++;
    return count;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={filterModalPaperProps(theme)}
    >
      <FilterModalHeader title="Filter Absensi Bulanan" onClose={onClose} />

      <DialogContent sx={filterModalContentSx(theme)}>
        {/* DATE RANGE FILTER */}
        {/* <Box>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            <DateRange sx={{ mr: 1 }} /> Filter Tanggal
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                >
                  Tanggal Awal
                </Typography>
                <DatePicker
                  selected={tanggalAwal}
                  onChange={(date) => setTanggalAwal(date)}
                  selectsStart
                  startDate={tanggalAwal}
                  endDate={tanggalAkhir}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Pilih tanggal awal"
                  customInput={
                    <TextField
                      fullWidth
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <DateRange sx={{ color: "text.secondary" }} />
                        ),
                      }}
                    />
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                >
                  Tanggal Akhir
                </Typography>
                <DatePicker
                  selected={tanggalAkhir}
                  onChange={(date) => setTanggalAkhir(date)}
                  selectsEnd
                  startDate={tanggalAwal}
                  endDate={tanggalAkhir}
                  minDate={tanggalAwal}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Pilih tanggal akhir"
                  customInput={
                    <TextField
                      fullWidth
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <DateRange sx={{ color: "text.secondary" }} />
                        ),
                      }}
                    />
                  }
                  locale="id"
                />
              </Box>
            </Grid>
          </Grid>

          {(tanggalAwal || tanggalAkhir) && (
            <Box
              sx={{ mt: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}
              >
                📅 Filter Tanggal Aktif:
              </Typography>
              <Typography variant="body2">
                {tanggalAwal
                  ? tanggalAwal.toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "..."}
                {" → "}
                {tanggalAkhir
                  ? tanggalAkhir.toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "..."}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider /> */}

        {/* FILTER */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
            🧭 Filter Kategori
          </Typography>
          <Grid container spacing={2}>
            {/* Bulan */}
            {getUniqueBulan().length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter Bulan</InputLabel>
                  <Select
                    multiple
                    value={selectedBulan}
                    onChange={(e) =>
                      setSelectedBulan(
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value
                      )
                    }
                    input={<OutlinedInput label="Filter Bulan" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((val) => (
                          <Chip key={val} label={val} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {getUniqueBulan().map((bulan) => (
                      <MenuItem key={bulan} value={bulan}>
                        <Checkbox checked={selectedBulan.indexOf(bulan) > -1} />
                        <ListItemText primary={bulan} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Cabang */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter Cabang</InputLabel>
                <Select
                  multiple
                  value={selectedCabang}
                  onChange={(e) =>
                    setSelectedCabang(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                    )
                  }
                  input={<OutlinedInput label="Filter Cabang" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {getUniqueCabang().map((cabang) => (
                    <MenuItem key={cabang} value={cabang}>
                      <Checkbox checked={selectedCabang.indexOf(cabang) > -1} />
                      <ListItemText primary={cabang} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <FilterModalActions
        onClearFilter={clearAllFilters}
        onClose={onClose}
        onApply={applyFilters}
        activeCount={getActiveFilterCount()}
      />
    </Dialog>
  );
};

export default CustomFilterModal;
